/**
 * Web component: Web video player (web-video-player)
 * -----------------------------------------------------------------------------
 * Web component video player to extend native video player functionality.
 *
 */
import './web-video-player.scss'

import breakpoints from '~/config/breakpoints'
import { vueReady } from '~/core/runners'

/**
 * Create new <web-video-player> custom element.
 */
class WebVideoPlayer extends HTMLElement {

  /**
   * Custom element constructor.
   * - Always call super first in constructor.
   */
  constructor() {
    super()
    this.autoplay = this.getAttribute('autoplay') === 'true'

    this.playing = {
      mobile: false,
      desktop: false,
    }

    this.sound = 'muted'

    this.nodeSelectors = {}

    this.selectors = {
      control: '[js-web-video-player="control"]',
      controlIcon: '[js-web-video-player="controlIcon"]',
      controlText: '[js-web-video-player="controlText"]',
      desktop: '[js-web-video-player="desktop"]',
      mobile: '[js-web-video-player="mobile"]',
      sound: '[js-web-video-player="sound"]',
      soundIcon: '[js-web-video-player="soundIcon"]',
      soundText: '[js-web-video-player="soundText"]',
    }
  }

  /**
   * Mounted.
   */
  connectedCallback() {
    this.setNodeSelectors()

    if (!this.nodeSelectors.desktop) {
      this.playing.desktop = 'none'
    }

    this.setEventListeners()
    this.setEventBusListeners()
    this.setIntersectionObserver()
  }

  /**
   * Set nodeSelectors.
   */
  setNodeSelectors() {
    this.nodeSelectors = {
      control: this.querySelector(this.selectors.control),
      controlIcon: this.querySelector(this.selectors.controlIcon),
      controlText: this.querySelector(this.selectors.controlText),
      desktop: this.querySelector(this.selectors.desktop),
      mobile: this.querySelector(this.selectors.mobile),
      sound: this.querySelector(this.selectors.sound),
      soundIcon: this.querySelector(this.selectors.soundIcon),
      soundText: this.querySelector(this.selectors.soundText),
    }
  }

  /**
   * Set event listeners.
   */
  setEventListeners() {
    this.nodeSelectors.mobile?.addEventListener('click', this.toggleVideos.bind(this))
    this.nodeSelectors.desktop?.addEventListener('click', this.toggleVideos.bind(this))

    this.nodeSelectors.control?.addEventListener('click', this.toggleVideos.bind(this))
    this.nodeSelectors.sound?.addEventListener('click', this.toggleSound.bind(this))
  }

  /**
   * Set EventBus listeners.
   */
  setEventBusListeners() {
    cnvs.EventBus.listen('cnvs:resize', () => {
      this.playVideos(true)
    })
  }

  /**
   * Toggle videos based on breakpoints.
   */
  toggleVideos() {
    if (
      this.nodeSelectors.desktop &&
      window.matchMedia(`(min-width: ${breakpoints.l})`).matches
    ) {
      if (this.playing.desktop) {
        this.pauseVideos()
        return
      }

      this.playVideos()
      return
    }

    if (this.playing.mobile) {
      this.pauseVideos()
      return
    }

    this.playVideos()
  }

  /**
   * Play videos based on breakpoints.
   * @param {Boolean} checkPlayState - Only play videos if video is already
   * playing (for resizing).
   */
  async playVideos(checkPlayState) {
    if (checkPlayState) {
      if (!this.playing.mobile && !this.nodeSelectors.desktop) {
        return
      }

      if (!this.playing.mobile && !this.playing.desktop) {
        return
      }
    }

    if (
      this.nodeSelectors.desktop &&
      window.matchMedia(`(min-width: ${breakpoints.l})`).matches
    ) {
      await this.pauseVideo('mobile')
      await this.playVideo('desktop')

    } else {
      await this.pauseVideo('desktop')
      await this.playVideo('mobile')
    }

    this.updatePlaybackState()
  }

  /**
   * Pause all videos.
   */
  async pauseVideos() {
    await this.pauseVideo('mobile')
    await this.pauseVideo('desktop')
    this.updatePlaybackState()
  }

  /**
   * Pause video.
   * @param {String} breakpoint - Breakpoint.
   * @returns {Promise}
   */
  pauseVideo(breakpoint) {
    const video = this.nodeSelectors[breakpoint]

    return new Promise(async(resolve) => {
      if (!video) {
        this.playing[breakpoint] = this.playing[breakpoint] === 'none' ? 'none' : false
        resolve()
        return
      }

      await video.pause()
      this.playing[breakpoint] = false
      resolve()
    })
  }

  /**
   * Pause video.
   * @param {String} breakpoint - Breakpoint.
   * @returns {Promise}
   */
  playVideo(breakpoint) {
    const video = this.nodeSelectors[breakpoint]

    return new Promise(async(resolve) => {
      if (!video) {
        this.playing[breakpoint] = this.playing[breakpoint] === 'none' ? 'none' : false
        resolve()
        return
      }

      await video.play()
      this.playing[breakpoint] = true
      resolve()
    })
  }

  /**
   * Update playback state of control.
   */
  updatePlaybackState() {
    const icon = this.playing.mobile === true || this.playing.desktop === true
      ? cnvs.icons.pause
      : cnvs.icons.play

    const text = this.playing.mobile === true || this.playing.desktop === true
      ? cnvs.strings.accessibility.video.pause
      : cnvs.strings.accessibility.video.play

    if (this.nodeSelectors.controlIcon && this.nodeSelectors.controlText) {
      this.nodeSelectors.controlIcon.innerHTML = icon
      this.nodeSelectors.controlText.innerText = text
    }
  }

  /**
   * Toggle sound.
   */
  async toggleSound() {
    if (this.sound === 'muted') {
      await this.unmuteVideos()
    } else {
      await this.muteVideos()
    }

    this.updateSoundState()
  }

  /**
   * Unmute videos.
   * @returns {Promise}
   */
  unmuteVideos() {
    return new Promise((resolve) => {
      this.nodeSelectors.mobile.muted = false

      if (this.nodeSelectors.desktop) {
        this.nodeSelectors.desktop.muted = false
      }

      this.sound = 'unmuted'
      resolve()
    })
  }

  /**
   * Mute videos.
   * @returns {Promise}
   */
  muteVideos() {
    return new Promise((resolve) => {
      this.nodeSelectors.mobile.muted = true

      if (this.nodeSelectors.desktop) {
        this.nodeSelectors.desktop.muted = true
      }

      this.sound = 'muted'
      resolve()
    })
  }

  /**
   * Update sound state of control.
   */
  updateSoundState() {
    const icon = this.sound === 'muted'
      ? cnvs.icons.mute
      : cnvs.icons.unmute

    const text = this.sound === 'muted'
      ? cnvs.strings.accessibility.video.unmute
      : cnvs.strings.accessibility.video.mute

    if (this.nodeSelectors.soundIcon && this.nodeSelectors.soundText) {
      this.nodeSelectors.soundIcon.innerHTML = icon
      this.nodeSelectors.soundText.innerText = text
    }
  }

  /**
   * Set intersection observer.
   */
  setIntersectionObserver() {
    if (!this.autoplay || 'IntersectionObserver' in window === false) {
      return
    }

    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5,
    }

    const observer = new IntersectionObserver(this.handleIntersection.bind(this), options)
    observer.observe(this)
  }

  /**
   * Handle intersection.
   * @param {Array} entries - Intersection entries.
   */
  handleIntersection(entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) {
        this.pauseVideos()
        return
      }

      this.playVideos()
    })
  }
}

/**
 * Define web component on Vue initialisation.
 */
vueReady(() => {
  if (customElements.get('web-video-player')) {
    return
  }

  customElements.define('web-video-player', WebVideoPlayer)
})
